import type { StoredHousehold, HouseholdUpsertInput } from '@/types/household';
import { fetchAPI } from './backendClient';

/**
 * Fetches the current user's household data from backend.
 * Returns null if no household exists.
 */
export async function fetchHousehold(): Promise<StoredHousehold | null> {
  const response = await fetchAPI('/v1/household/');

  if (response.status === 403) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch household: ${response.statusText}`);
  }

  const data = await response.json();

  if (data && !data.id) {
    throw new Error('Invalid household data received from backend');
  }

  return data as StoredHousehold | null;
}

/**
 * Creates or updates the current user's household data in backend.
 * Backend automatically handles both create and update cases.
 */
export async function upsertHousehold(
  household: HouseholdUpsertInput
): Promise<StoredHousehold> {
  const response = await fetchAPI('/v1/household/', {
    method: 'POST',
    body: JSON.stringify(household),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to save household: ${response.statusText}`);
  }

  const data = await response.json();

  if (!data || !data.id) {
    throw new Error('Invalid household data received from backend');
  }

  return data as StoredHousehold;
}

/**
 * Deletes the current user's household data from backend.
 */
export async function deleteHousehold(): Promise<void> {
  const response = await fetchAPI('/v1/household/', {
    method: 'DELETE',
  });

  if (!response.ok && response.status !== 204) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to delete household: ${response.statusText}`);
  }
}
